import { AppContext } from 'next/app';
import {
  getPageProps,
  performRedirect,
} from '@sls-react/lib/initialData/common';
import { ClientLRU, InitialData } from '@sls-react/lib/initialData/initialData';
import LRUCache from 'lru-cache';
import { SaleInitialState } from '@sls-react/contexts/SaleContext';
import { KnownCookies } from '@sls-react/typings/cookies';
import { parseCookies } from 'nookies';

const clientLru = new LRUCache<string, any>();

function getFromCache<K extends keyof ClientLRU>(
  key: K,
): ClientLRU[K] | undefined {
  return clientLru.get(key);
}

function setIntoCache<K extends keyof ClientLRU>(
  key: K,
  value?: ClientLRU[K],
): void {
  clientLru.set(key, value);
}

function getCachedInitialData(
  appCtx: AppContext,
): Omit<InitialData, 'pageProps'> {
  const cookies: KnownCookies =
    appCtx.ctx.req?.['cookies'] || parseCookies(appCtx.ctx);
  const sales: SaleInitialState = {
    dismissed: !!cookies.dismissed_sale,
  };

  return {
    sales,
    abTests: getFromCache('abTests')!,
    chromeless: getFromCache('chromeless')!,
    initialSessionState: getFromCache('initialSessionState')!,
  };
}

export function setCacheOnClient(props: InitialData) {
  setIntoCache('initialSessionState', props.initialSessionState);
  setIntoCache('chromeless', props.chromeless);
  setIntoCache('abTests', props.abTests);
}

export default async function getInitialClientProps(
  appCtx: AppContext,
): Promise<InitialData> {
  const initialData = await getCachedInitialData(appCtx);

  await performRedirect(appCtx, initialData.initialSessionState);

  const pageProps = await getPageProps(appCtx);

  return {
    ...initialData,
    pageProps,
  };
}
